import type { EmployeeField, SegmentRule } from "#shared/segmentsTypes.ts";
import { Skeleton } from "@/components/ui/skeleton.tsx";
import { Tag, TagGroup, TagList } from "@/components/ui/tag-group.tsx";
import { Tooltip, TooltipContent } from "@/components/ui/tooltip.tsx";
import { useI18nextInstance } from "@squide/i18next";
import { useCallback } from "react";
import type { Selection } from "react-aria-components";
import { useTranslation } from "react-i18next";
import { segmentsI18NextInstanceKey } from "../../i18n/i18next.ts";
import { useRuleSummaries } from "./useRuleSummaries.ts";

export interface AppliedRulePillsProps {
    rules: SegmentRule[];
    fields: EmployeeField[];
    "aria-label": string;
    onRemove: (rule: SegmentRule) => void;
}

export function AppliedRulePills({ rules, fields, "aria-label": ariaLabel, onRemove }: AppliedRulePillsProps) {
    const i18nextInstance = useI18nextInstance(segmentsI18NextInstanceKey);
    const { t } = useTranslation("segmentRules", { i18n: i18nextInstance });
    const summaries = useRuleSummaries(rules, fields);

    const handleRemove = useCallback(
        (keys: Selection) => {
            if (keys === "all") {
                return;
            }

            for (const key of keys) {
                const summary = summaries.find(entry => entry.id === key);

                if (summary) {
                    onRemove(summary.rule);
                }
            }
        },
        [onRemove, summaries]
    );

    if (summaries.length === 0) {
        return null;
    }

    return (
        <>
            <output className="sr-only">{summaries.some(summary => summary.isLoading) && t("loadingRules")}</output>
            <TagGroup aria-label={ariaLabel} onRemove={handleRemove}>
                <TagList items={summaries}>
                    {summary => {
                        if (summary.isLoading) {
                            return (
                                <Tag id={summary.id} textValue={summary.fieldName ?? t("property.unavailable")} className="h-11 px-3 text-sm/6 *:[svg]:size-4">
                                    <Skeleton isLoading inert aria-hidden="true">
                                        <span className="inline-block h-4 w-24" />
                                    </Skeleton>
                                </Tag>
                            );
                        }

                        const text = summary.label ?? (summary.fieldName ? t("ruleUnavailable", { field: summary.fieldName }) : t("property.unavailable"));

                        return (
                            <Tooltip delay={300}>
                                <Tag id={summary.id} textValue={text} className="h-11 px-3 text-sm/6 *:[svg]:size-4">
                                    <span className="max-w-80 truncate">{text}</span>
                                </Tag>
                                <TooltipContent>{text}</TooltipContent>
                            </Tooltip>
                        );
                    }}
                </TagList>
            </TagGroup>
        </>
    );
}
